import { useMemo, useState } from 'react';
import type { ScheduledEmailRes } from '@dealstack/shared/lib/backendClient';
import { useScheduledEmails } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useScheduledEmails.tsx';
import { useScheduledEmailActions } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useScheduledEmailActions.tsx';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations.ts';
import { BasicMantineTopToolbar } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTopToolbar';
import { LabeledSegmentedControl } from '@dealstack/frontend/shared/components/LabeledSegmentedControl/LabeledSegmentedControl.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { getScheduledQueueColumns } from './getScheduledQueueColumns';
import { RescheduleModal } from './RescheduleModal';
import { ScheduledQueueConfirmModal } from './ScheduledQueueConfirmModal';
import {
	SCHEDULED_QUEUE_STATUS_FILTER_TO_QUERY_STATUS,
	ScheduledQueueStatusFilter,
} from './ScheduledQueueTab.types';
import type { ConfirmAction, ConfirmState, RescheduleTarget } from './ScheduledQueueTab.types';
import { ScheduledQueueTabContainer } from './styles';

// Stable ref: an inline `schedules ?? []` mints a new array each render, tripping
// MantineTable's data useEffect into an infinite loop. Mirrors EMPTY_FUNDS.
const EMPTY_SCHEDULES: ScheduledEmailRes[] = [];

export const ScheduledQueueTab = () => {
	const tMipManage = useTranslationMipManage();
	const { dealId, mipId: programmeId } = useRequiredParams('dealId', 'mipId');

	const [statusFilter, setStatusFilter] = useState<ScheduledQueueStatusFilter>(
		ScheduledQueueStatusFilter.ALL
	);
	const [rescheduleTarget, setRescheduleTarget] = useState<RescheduleTarget | null>(null);
	const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

	const { data: schedules, isLoading } = useScheduledEmails({
		dealId,
		programmeId,
		status: SCHEDULED_QUEUE_STATUS_FILTER_TO_QUERY_STATUS[statusFilter],
	});

	const {
		cancel,
		isCancelPending,
		reschedule,
		isReschedulePending,
		restart,
		isRestartPending,
		sendNow,
		isSendNowPending,
	} = useScheduledEmailActions({
		dealId,
		programmeId,
		onSettled: () => {
			setConfirmState(null);
			setRescheduleTarget(null);
		},
	});

	const columns = useMemo(
		() =>
			getScheduledQueueColumns({
				tMipManage,
				handlers: {
					onSendNow: (schedule) => setConfirmState({ action: 'send-now', schedule }),
					onReschedule: (schedule) =>
						setRescheduleTarget({ id: schedule.id, nextFireAt: schedule.nextFireAt }),
					onCancel: (schedule) => setConfirmState({ action: 'cancel', schedule }),
					onRestart: (schedule) => setConfirmState({ action: 'restart', schedule }),
				},
			}),
		[tMipManage]
	);

	const mantineTableDataOperations = useMantineTableDataOperations({
		initialPagination: { pageIndex: 0, pageSize: 10 },
	});

	const statusFilterOptions = useMemo(
		() => [
			{
				label: tMipManage('automated_emails.scheduled_queue.status_filter.all'),
				value: ScheduledQueueStatusFilter.ALL,
			},
			{
				label: tMipManage('automated_emails.scheduled_queue.status_filter.active'),
				value: ScheduledQueueStatusFilter.ACTIVE,
			},
			{
				label: tMipManage('automated_emails.scheduled_queue.status_filter.completed'),
				value: ScheduledQueueStatusFilter.COMPLETED,
			},
			{
				label: tMipManage('automated_emails.scheduled_queue.status_filter.cancelled'),
				value: ScheduledQueueStatusFilter.CANCELLED,
			},
			{
				label: tMipManage('automated_emails.scheduled_queue.status_filter.failed'),
				value: ScheduledQueueStatusFilter.FAILED,
			},
		],
		[tMipManage]
	);

	const handleConfirm = () => {
		if (!confirmState) {
			return;
		}
		const { action, schedule } = confirmState;
		if (action === 'send-now') {
			sendNow(schedule.id);
		} else if (action === 'cancel') {
			cancel(schedule.id);
		} else {
			restart(schedule.id);
		}
	};

	const confirmModalCopy: Record<
		ConfirmAction,
		{ title: string; description: string; confirmLabel: string }
	> = {
		'send-now': {
			title: tMipManage('automated_emails.scheduled_queue.confirm_modal.send_now.title'),
			description: tMipManage(
				'automated_emails.scheduled_queue.confirm_modal.send_now.description'
			),
			confirmLabel: tMipManage('automated_emails.scheduled_queue.actions.send_now'),
		},
		cancel: {
			title: tMipManage('automated_emails.scheduled_queue.confirm_modal.cancel.title'),
			description: tMipManage(
				'automated_emails.scheduled_queue.confirm_modal.cancel.description'
			),
			confirmLabel: tMipManage('automated_emails.scheduled_queue.actions.cancel'),
		},
		restart: {
			title: tMipManage('automated_emails.scheduled_queue.confirm_modal.restart.title'),
			description: tMipManage(
				'automated_emails.scheduled_queue.confirm_modal.restart.description'
			),
			confirmLabel: tMipManage('automated_emails.scheduled_queue.actions.restart'),
		},
	};

	const pendingByAction: Record<ConfirmAction, boolean> = {
		'send-now': isSendNowPending,
		cancel: isCancelPending,
		restart: isRestartPending,
	};
	const isConfirmSubmitting = confirmState ? pendingByAction[confirmState.action] : false;

	return (
		<ScheduledQueueTabContainer>
			<MantineTable<ScheduledEmailRes>
				columns={columns}
				data={schedules ?? EMPTY_SCHEDULES}
				isLoading={isLoading}
				dataOperations={mantineTableDataOperations}
				getRowId={(row) => row.id}
				enableRowSelection={false}
				enableRowActions={false}
				manualFiltering={false}
				renderTopToolbar={({ table }) => (
					<BasicMantineTopToolbar
						table={table}
						hideFiltersButton
						customActions={
							<LabeledSegmentedControl<ScheduledQueueStatusFilter>
								value={statusFilter}
								onChange={setStatusFilter}
								data={statusFilterOptions}
								data-testid="scheduled-queue-status-filter"
							/>
						}
					/>
				)}
			/>

			{rescheduleTarget && (
				<RescheduleModal
					initialNextFireAt={rescheduleTarget.nextFireAt}
					isSubmitting={isReschedulePending}
					onConfirm={(nextFireAt) => reschedule(rescheduleTarget.id, nextFireAt)}
					onClose={() => setRescheduleTarget(null)}
				/>
			)}

			{confirmState && (
				<ScheduledQueueConfirmModal
					title={confirmModalCopy[confirmState.action].title}
					description={confirmModalCopy[confirmState.action].description}
					confirmLabel={confirmModalCopy[confirmState.action].confirmLabel}
					isSubmitting={isConfirmSubmitting}
					onConfirm={handleConfirm}
					onClose={() => setConfirmState(null)}
				/>
			)}
		</ScheduledQueueTabContainer>
	);
};
